import { Components, Theme } from '@mui/material/styles';

/**
 * Material-UI component style overrides for Yugen Frontend
 * Applies dark theme styling with gold accents
 */
export const components: Components<Theme> = {
  // Button overrides
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: '24px', // Pill-shaped buttons
        padding: '10px 24px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'all 0.3s ease',
      },
      contained: ({ theme }) => ({
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
        color: theme.palette.primary.contrastText,
        boxShadow: `0 4px 12px ${theme.palette.primary.main}4D`,
        '&:hover': {
          background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
          boxShadow: `0 6px 20px ${theme.palette.primary.main}66`,
          transform: 'translateY(-2px)',
        },
        '&:active': {
          transform: 'translateY(0)',
        },
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
        borderWidth: '2px',
        '&:hover': {
          borderWidth: '2px',
          borderColor: theme.palette.primary.light,
          backgroundColor: `${theme.palette.primary.main}14`,
        },
      }),
      text: ({ theme }) => ({
        color: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}14`,
        },
      }),
    },
  },

  // Card overrides
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        borderRadius: '12px',
        border: `1px solid ${theme.palette.primary.main}1A`,
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: `${theme.palette.primary.main}66`,
          boxShadow: `0 8px 24px ${theme.palette.primary.main}26`,
          transform: 'translateY(-4px)',
        },
      }),
    },
  },

  // AppBar overrides
  MuiAppBar: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: `${theme.palette.background.default}CC`,
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${theme.palette.primary.main}1A`,
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      }),
    },
  },

  // TextField overrides
  MuiTextField: {
    styleOverrides: {
      root: ({ theme }) => ({
        '& .MuiOutlinedInput-root': {
          backgroundColor: theme.palette.background.paper,
          borderRadius: '8px',
          '& fieldset': {
            borderColor: `${theme.palette.primary.main}33`,
          },
          '&:hover fieldset': {
            borderColor: `${theme.palette.primary.main}66`,
          },
          '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
          },
        },
        '& .MuiInputLabel-root': {
          color: theme.palette.text.secondary,
          '&.Mui-focused': {
            color: theme.palette.primary.main,
          },
        },
      }),
    },
  },

  // Paper overrides
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        backgroundImage: 'none',
      }),
      elevation1: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
      },
      elevation2: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
      },
      elevation3: {
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.5)',
      },
    },
  },

  // Chip overrides
  MuiChip: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: `${theme.palette.primary.main}1A`,
        color: theme.palette.primary.light,
        borderRadius: '16px',
        fontWeight: 500,
      }),
      filled: ({ theme }) => ({
        backgroundColor: `${theme.palette.primary.main}26`,
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}40`,
        },
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.primary.main,
        color: theme.palette.primary.main,
      }),
    },
  },

  // Divider overrides
  MuiDivider: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderColor: `${theme.palette.primary.main}1F`,
      }),
    },
  },

  // Link overrides
  MuiLink: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.primary.main,
        textDecoration: 'none',
        transition: 'color 0.2s ease',
        '&:hover': {
          color: theme.palette.primary.light,
          textDecoration: 'underline',
        },
      }),
    },
  },

  // IconButton overrides
  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.primary.main,
        transition: 'all 0.2s ease',
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}14`,
          color: theme.palette.primary.light,
        },
      }),
    },
  },

  // Tooltip overrides
  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        border: `1px solid ${theme.palette.primary.main}4D`,
        fontSize: '0.875rem',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      }),
      arrow: ({ theme }) => ({
        color: theme.palette.background.paper,
        '&::before': {
          border: `1px solid ${theme.palette.primary.main}4D`,
        },
      }),
    },
  },

  // Dialog overrides
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        backgroundImage: 'none',
        border: `1px solid ${theme.palette.primary.main}33`,
      }),
    },
  },

  // Menu overrides
  MuiMenu: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.primary.main}33`,
      }),
    },
  },

  // MenuItem overrides
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&:hover': {
          backgroundColor: `${theme.palette.primary.main}14`,
        },
        '&.Mui-selected': {
          backgroundColor: `${theme.palette.primary.main}26`,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}33`,
          },
        },
      }),
    },
  },
};
